import React, { useState, useRef, useEffect } from 'react';
import { messagingService } from '../../../services/messagingService';
import { Send, Paperclip, Smile, AtSign, Pin, Megaphone, Users, Clock, Check, CheckCheck, AlertCircle, Crown, Shield } from 'lucide-react';

const GroupMessagePanel = ({ 
  group, 
  messages, 
  onSendMessage, 
  loading, 
  currentUser, 
  userProfile,
  canModerate
}) => {
  const [messageText, setMessageText] = useState('');
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastToSMS, setBroadcastToSMS] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const messageSubscriptionRef = useRef(null);

  // Auto scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Subscribe to real-time message updates
  useEffect(() => {
    if (group?.id) {
      messageSubscriptionRef.current = messagingService?.subscribeToMessages(
        group?.id,
        (payload) => {
          // Handle real-time message updates
          console.log('Real-time message update:', payload);
        }
      );
    }

    return () => {
      if (messageSubscriptionRef?.current) {
        messageSubscriptionRef?.current?.unsubscribe();
      }
    };
  }, [group?.id]);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!messageText?.trim() || sending) return;

    setSending(true);
    try {
      let phoneNumbers = [];
      
      // If broadcasting to SMS, collect phone numbers
      if (broadcastToSMS && group?.participants) {
        phoneNumbers = group?.participants?.filter(p => p?.user?.phone)?.map(p => p?.user?.phone);
      }

      await onSendMessage(messageText?.trim(), phoneNumbers);
      setMessageText('');
      setShowBroadcast(false);
      setBroadcastToSMS(false);
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp)?.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMessageStatusIcon = (status) => {
    switch (status) {
      case 'sending':
        return <Clock className="w-3 h-3 text-gray-400" />;
      case 'sent':
        return <Check className="w-3 h-3 text-gray-500" />;
      case 'delivered':
        return <CheckCheck className="w-3 h-3 text-green-500" />;
      case 'failed':
        return <AlertCircle className="w-3 h-3 text-red-500" />;
      default:
        return null;
    }
  };

  const getRoleIcon = (role) => {
    switch (role?.toLowerCase()) {
      case 'city_official': case'city official':
        return <Shield className="w-3 h-3 text-green-600" />;
      case 'pcf_admin': case'pcf admin':
        return <Crown className="w-3 h-3 text-purple-600" />;
      default:
        return null;
    }
  };

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'city_official': case'city official':
        return 'text-green-600';
      case 'pcf_admin': case'pcf admin':
        return 'text-purple-600';
      case 'business':
        return 'text-orange-600';
      default:
        return 'text-blue-600';
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-500">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ height: '600px' }}>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages?.length === 0 ? (
          <div className="text-center py-8">
            <Users className="mx-auto h-8 w-8 text-gray-400 mb-2" />
            <p className="text-gray-500">Welcome to the team group chat!</p>
            <p className="text-sm text-gray-400 mt-1">Start the conversation with your team.</p>
          </div>
        ) : (
          messages?.map((message, index) => {
            const isOwnMessage = message?.senderId === currentUser?.id;
            const showSender = index === 0 || messages?.[index - 1]?.senderId !== message?.senderId;
            const senderRole = message?.sender?.role;

            return (
              <div key={message?.id} className="flex flex-col space-y-1">
                {showSender && !isOwnMessage && (
                  <div className="flex items-center space-x-2 px-2">
                    <span className={`text-sm font-medium ${getRoleColor(senderRole)}`}>
                      {message?.sender?.fullName || 'Unknown'}
                    </span>
                    {getRoleIcon(senderRole)}
                    <span className="text-xs text-gray-500">
                      {formatTime(message?.createdAt)}
                    </span>
                  </div>
                )}
                <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                    isOwnMessage
                      ? 'bg-green-600 text-white' :'bg-gray-100 text-gray-900'
                  }`}>
                    <p className="text-sm">{message?.content}</p>
                    <div className={`flex items-center justify-between mt-1 ${
                      isOwnMessage ? 'text-green-100' : 'text-gray-500'
                    }`}>
                      {isOwnMessage && (
                        <>
                          <span className="text-xs">{formatTime(message?.createdAt)}</span>
                          <div className="ml-2">
                            {getMessageStatusIcon(message?.messageStatus)}
                          </div>
                        </>
                      )}
                    </div>
                    {message?.phoneNumber && (
                      <div className={`text-xs mt-1 ${
                        isOwnMessage ? 'text-green-200' : 'text-gray-600'
                      }`}>
                        📱 Broadcast SMS sent
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>
      {/* Message Input */}
      <div className="p-4 border-t border-gray-200">
        {/* Broadcast Options */}
        {canModerate && showBroadcast && (
          <div className="mb-3 p-3 bg-orange-50 rounded-lg">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <Megaphone className="w-4 h-4 text-orange-600 mr-2" />
                <span className="text-sm font-medium text-orange-700">Broadcast Mode</span>
              </div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={broadcastToSMS}
                  onChange={(e) => setBroadcastToSMS(e?.target?.checked)}
                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="ml-2 text-sm text-orange-700">Send SMS to all team members</span>
              </label>
            </div>
            <button
              onClick={() => setShowBroadcast(false)}
              className="mt-2 text-sm text-orange-600 hover:text-orange-800"
            >
              Cancel broadcast
            </button>
          </div>
        )}

        <form onSubmit={handleSendMessage} className="flex items-end space-x-3">
          <div className="flex-1">
            <div className="relative">
              <textarea
                value={messageText}
                onChange={(e) => setMessageText(e?.target?.value)}
                onKeyPress={(e) => {
                  if (e?.key === 'Enter' && !e?.shiftKey) {
                    e?.preventDefault();
                    handleSendMessage(e);
                  }
                }}
                placeholder={showBroadcast ? "Type announcement message..." : "Type your message..."}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
                rows="1"
                style={{ minHeight: '40px', maxHeight: '120px' }}
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {canModerate && (
              <button
                type="button"
                onClick={() => setShowBroadcast(!showBroadcast)}
                className={`p-2 rounded-full transition-colors ${
                  showBroadcast 
                    ? 'bg-orange-100 text-orange-600' :'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
                title="Broadcast announcement"
              >
                <Megaphone className="w-5 h-5" />
              </button>
            )}

            <button
              type="button"
              className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              title="Mention someone"
            >
              <AtSign className="w-5 h-5" />
            </button>

            <button
              type="button"
              className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
            >
              <Paperclip className="w-5 h-5" />
            </button>

            <button
              type="button"
              className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
            >
              <Smile className="w-5 h-5" />
            </button>

            <button
              type="submit"
              disabled={!messageText?.trim() || sending}
              className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </form>

        {/* Quick Actions for Moderators */}
        {canModerate && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <button className="flex items-center hover:text-gray-700">
                <Pin className="w-4 h-4 mr-1" />
                Pin Message
              </button>
              <button className="flex items-center hover:text-gray-700">
                <Users className="w-4 h-4 mr-1" />
                Manage Members
              </button>
            </div>
            <div className="text-xs text-gray-400">
              Moderator privileges active
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupMessagePanel;